import ConstantHelper from "~helpers/ConstantHelper";
import IFrameHelper from "~helpers/IFrameHelper";
import SanitizeHelper from "~helpers/SanitizeHelper";

export const [popupPosition, popupPositionOptions, popupPositionMap] = ConstantHelper.typify({
  bottomRight: window.t("constants.popupPosition.bottomRight"),
  bottomLeft: window.t("constants.popupPosition.bottomLeft"),
});

export const [popupFooterMode, popupFooterModeOptions, popupFooterModeMap] = ConstantHelper.typify({
  hidden: window.t("constants.popupFooterMode.hidden"),
  visible: window.t("constants.popupFooterMode.visible"),
});

export const [popupMinimizeExistingChat, popupMinimizeExistingChatOptions, popupMinimizeExistingChatMap] =
  ConstantHelper.typify({
    minimized: window.t("constants.popupMinimizeExistingChat.minimized"),
    preventState: window.t("constants.popupMinimizeExistingChat.preventState"),
  });

export const [popupMinimizeBehavior, popupMinimizeBehaviorOptions, popupMinimizeBehaviorMap] = ConstantHelper.typify({
  terminateOnClose: window.t("constants.popupMinimizeBehavior.terminateOnClose"),
  minimizeOnClose: window.t("constants.popupMinimizeBehavior.minimizeOnClose"),
});

export const [popupAvatarVisibility, popupAvatarVisibilityOptions, popupAvatarVisibilityMap] = ConstantHelper.typify({
  hidden: window.t("constants.popupAvatarVisibility.hidden"),
  visible: window.t("constants.popupAvatarVisibility.visible"),
});

export const [popupSpeechRecognition, popupSpeechRecognitionOptions, popupSpeechRecognitionMap] = ConstantHelper.typify(
  {
    disabled: window.t("constants.popupSpeechRecognition.disabled"),
    enabled: window.t("constants.popupSpeechRecognition.enabled"),
    voiceRecord: window.t("constants.popupSpeechRecognition.voiceRecord"),
  }
);

export const [popupTriggerType, popupTriggerTypeOptions, popupTriggerTypeMap] = ConstantHelper.typify({
  instant: window.t("constants.popupTriggerType.instant"),
  delayed: window.t("constants.popupTriggerType.delayed"),
  scroll: window.t("constants.popupTriggerType.scroll"),
});

export const popupIconSizeDefault = 62;
export const popupIconMarginDefault = 25;
export const popupChatHeightDefault = 645;
export const popupChatWidthDefault = 375;
export const popupLottieOffsetXDefault = 0;
export const popupLottieOffsetYDefault = 0;
export const popupAudioFilesRegex = /(audio\/(mp3|mpeg|ogg|wav|webm))/i;
export const [popupIconType, popupIconTypeOptions, popupIconTypeMap] = ConstantHelper.typify({
  none: window.t("constants.popupIconType.none"),
  default: window.t("constants.popupIconType.default"),
  icon: window.t("constants.popupIconType.icon"),
  lottie: window.t("constants.popupIconType.lottie"),
});

export const popupSettingsMapper = (settings = {}) => {
  const {
    popup_button_type,
    popup_header_icon_type,
    popup_bot_icon_type,
    popup_style_info,
    chatbot_info,
    system_chatbot_info,
    ...rest
  } = settings;

  const styleInfo = popup_style_info || {};
  const chatbotInfo = chatbot_info || {};
  const systemChatbotInfo = system_chatbot_info || {};
  const mapped = {
    ...rest,
    popup_style_info: {
      ...styleInfo,
      popup_icon_type:
        styleInfo.popup_icon_type ?? (popup_button_type === "IC" ? popupIconType.icon : popupIconType.default),
      popup_header_icon_type:
        styleInfo.popup_header_icon_type ??
        (popup_header_icon_type === "IC" ? popupIconType.icon : popupIconType.default),
      popup_bot_icon_type:
        styleInfo.popup_bot_icon_type ?? (popup_bot_icon_type === "IC" ? popupIconType.icon : popupIconType.default),

      popup_position: styleInfo.popup_position ?? popupPosition.bottomRight,
      popup_icon_size: styleInfo.popup_icon_size ?? popupIconSizeDefault,

      /** @deprecated Use popup_margin_x and popup_margin_y instead and remove popup_margin on save */
      popup_margin: styleInfo.popup_margin ?? popupIconMarginDefault,
      popup_margin_x: styleInfo.popup_margin_x ?? popupIconMarginDefault,
      popup_margin_y: styleInfo.popup_margin_y ?? popupIconMarginDefault,
      chat_height: styleInfo.chat_height ?? popupChatHeightDefault,
      chat_width: styleInfo.chat_width ?? popupChatWidthDefault,
      lottie_width: styleInfo.lottie_width ?? popupIconSizeDefault,
      lottie_height: styleInfo.lottie_height ?? popupIconSizeDefault,
      lottie_offset_x: styleInfo.lottie_offset_x ?? popupLottieOffsetXDefault,
      lottie_offset_y: styleInfo.lottie_offset_y ?? popupLottieOffsetYDefault,
      lottie_loop_delay: styleInfo.lottie_loop_delay ?? 0,
      lottie_loop_speed: styleInfo.lottie_loop_speed ?? 1,
      popup_ballon_timeout: styleInfo.popup_ballon_timeout ?? 0,
      popup_trigger_type: styleInfo.popup_trigger_type ?? popupTriggerType.instant,
      popup_trigger_type_payload: styleInfo.popup_trigger_type_payload ?? 0,
    },
    chatbot_info: {
      ...chatbotInfo,
      avatar_visibility: chatbotInfo.avatar_visibility ?? popupAvatarVisibility.visible,
      footer_mode: chatbotInfo.footer_mode ?? popupFooterMode.visible,
      speech_recognition_mode: chatbotInfo.speech_recognition_mode ?? popupSpeechRecognition.enabled,
      speech_recognition_auto_send: chatbotInfo.speech_recognition_auto_send ?? false,
      //Set default value if expected value is not in the map.(This is for backward compatibility with old data)
      minimize_existing_Chat: popupMinimizeExistingChatMap[chatbotInfo.minimize_existing_Chat]
        ? chatbotInfo.minimize_existing_Chat
        : popupMinimizeExistingChat.minimized,
      minimize_behavior: chatbotInfo.minimize_behavior ?? popupMinimizeBehavior.terminateOnClose,
      disable_lazy_load: chatbotInfo.disable_lazy_load ?? false,
      customer_insight_enabled: chatbotInfo.customer_insight_enabled ?? false,
    },
    system_chatbot_info: {
      ...systemChatbotInfo,
      enable_footer_customization: systemChatbotInfo.enable_footer_customization ?? false,
      enable_css_customization: systemChatbotInfo.enable_css_customization ?? false,
    },
  };
  if (IFrameHelper.isWebChatMode()) {
    const stylePrefix = "style__";
    const urlParams = new URLSearchParams(window.location.search);

    const styleParamsEntries = [...urlParams.entries()].filter(([key, value]) => key.startsWith(stylePrefix) && value);
    const rootStyleKeyMap = [["popup_primary_color", "primary_color"]];
    const newStyleObj = {};
    const newStyleRootObj = {};
    for (const [keyRaw, valueRaw] of styleParamsEntries) {
      const key = SanitizeHelper.safeText(keyRaw);
      const value = SanitizeHelper.safeText(valueRaw);
      const styleKey = key.replace(stylePrefix, "");
      const isNumber = !isNaN(value);

      const styleValue = isNumber ? Number(value) : value;
      if (rootStyleKeyMap.some(([_originalKey, paramKey]) => paramKey === styleKey)) {
        const originalKey = rootStyleKeyMap.find(([_originalKey, paramKey]) => paramKey === styleKey)[0];
        newStyleRootObj[originalKey] = styleValue;
      } else {
        newStyleObj[styleKey] = styleValue;
      }
    }
    Object.assign(mapped.popup_style_info, newStyleObj);
    Object.assign(mapped, newStyleRootObj);
  }

  return mapped;
};
